'use client';
import FourOFour from '@src/app/not-found';
import VotedCard from '@src/components/cards/votedCard';
import BackArrow from '@src/components/icons/back';
import GovernanceActionDetailsLeftContainer from '@src/components/molecules/governance-action-detail/leftContainer';
import { GovernanceActionType } from '@src/models/enum';
import type {
  IGovernanceAction,
  IProposalVoterType,
  Voter,
} from '@src/models/types';
import { useLazyGetProposalByIdQuery } from '@src/store/governanceAction/api';
import {
  areCCVoteTotalsDisplayed,
  areSPOVoteTotalsDisplayed,
  extractIndex,
  getGovActionVotingThresholdKey,
  getVoteResponse,
} from '@src/utils/governanceActionUtils';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { MetadataValidationResponse } from '@src/models/dtos/metadata';
import { fetchProposalMetadata } from '@src/utils/governanceActionUtils';
import cn from 'classnames';
import { getCip30Wallet } from '@src/utils/kuberUtils';
import { useAppSelector } from '@src/store/hooks';
import {
  useLazyGetDRepByIdQuery,
  useLazyGetDRepVotesQuery,
} from '@src/store/drep/api';
import { getDRepIdFromCip30Wallet } from '@src/utils/dRepUtils';
import { isEmpty } from 'lodash';
import { Check } from '@src/components/icons/check';
import { useModal } from '@src/components/modal-views/context';
import Button from '@src/components/atoms/buttons/button';
import FiveOThree from '@src/app/503';
import { simpleFetch } from '@src/utils/fetch';
import ChevronDown from '@src/components/icons/chevronDown';
import { drepVoteCommentErrorMessage } from '@src/constants';

export default function GovernanceActionDetailsPage({
  id,
  hasServerHeader = false,
}: any) {
  const [isMounted, setIsMounted] = useState(false);
  const [governanceAction, setGovernanceAction] =
    useState<IGovernanceAction | null>(null);
  const [getProposal, { isLoading, isError, error }] =
    useLazyGetProposalByIdQuery();
  const [metadataResponse, setMetadataResponse] =
    useState<MetadataValidationResponse | null>(null);
  const router = useRouter();
  const [isLoadingProposal, setIsLoadingProposal] = useState(true);
  const wallet = useAppSelector((state) => state.wallet);
  const [voteDetails, setVoteDetails] = useState({
    type: '',
    comment: '',
  });
  // const [votedType, setVotedType] = useState<string>('');
  // const [voteContext , setV]
  const [isGetDRepDetailsAndVoteLoading, setIsGetDRepDetailsAndVoteLoading] =
    useState<boolean>(true);
  const [getDRepDetails] = useLazyGetDRepByIdQuery();
  const [getDRepVotes] = useLazyGetDRepVotesQuery();
  const [isdRepRegistered, setIsDRepRegistered] = useState<boolean>(false);
  const [dRepId, setDRepId] = useState<string>('');
  const { openModal } = useModal();
  const pendingTransactions = useAppSelector(
    (state) => state.pendingTransactions
  );
  const epochParams = useAppSelector((state) => state.config.protocolParams);

  const [showFullVoteComment, setShowFullVoteComment] = useState(false);
  const [errorFetchingVoteComment, setErrorFetchingVoteComment] =
    useState(false);

  useEffect(() => {
    setIsMounted(true);

    const fetchProposal = async () => {
      const index = extractIndex(window.location.href);
      const governanceActionWithIndex = index ? `${id}#${index}` : `${id}`;
      const response = await getProposal(
        governanceActionWithIndex.includes('#')
          ? encodeURIComponent(governanceActionWithIndex)
          : governanceActionWithIndex
      ).unwrap();
      setGovernanceAction(response);
      // Render now. This response already carries the whole metadata document
      // from the index — title, abstract, motivation, rationale — so there is
      // nothing to wait for before showing the page.
      setIsLoadingProposal(false);

      // Fetching the anchor document is a separate concern: it validates the
      // on-chain hash and adds the supporting links. It goes over IPFS, which
      // is slow and frequently fails, and the page used to block on it — and
      // then render a 404 when it came back empty.
      void fetchProposalMetadata(response.proposal)
        .then(setMetadataResponse)
        .catch(() => setMetadataResponse(null));
    };

    const fetchDRepDetailsAndVotedType = async () => {
      try {
        const walletFromKuber = await getCip30Wallet(wallet);
        if (!walletFromKuber) {
          console.error('Failed to fetch wallet from Kuber');
          return;
        }

        const dRepId = await getDRepIdFromCip30Wallet(walletFromKuber);

        if (!dRepId) return;
        setDRepId(dRepId);

        const dRepResponse = await getDRepDetails(dRepId).unwrap();
        if (dRepResponse.dRep) {
          const index = extractIndex(window.location.href);
          const governanceActionWithIndex = index
            ? `${id}#${index}`
            : decodeURIComponent(`${id}`);
          const registeredDRep = dRepResponse.dRep.status !== 'Retired';
          setIsDRepRegistered(registeredDRep);
          const dRepVoteResponse = await getDRepVotes({
            dRepId,
            params: { page: 1, size: 100 },
          });
          if (dRepVoteResponse.data?.items) {
            for (const vote of dRepVoteResponse.data.items) {
              if (vote.govActionId === governanceActionWithIndex) {
                setVoteDetails({ type: vote.voteType, comment: '' });

                if (vote.voteAnchorUrl) {
                  try {
                    const voteMetaData = await simpleFetch(vote.voteAnchorUrl);
                    if (voteMetaData.body.comment) {
                      setVoteDetails((prev) => ({
                        ...prev,
                        comment: voteMetaData.body.comment,
                      }));
                    }
                  } catch (err) {
                    setErrorFetchingVoteComment(true);
                    console.error('Failed to fetch vote metadata:', err);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch DRep details:', error);
      } finally {
        setIsGetDRepDetailsAndVoteLoading(false);
      }
    };

    if (typeof window !== 'undefined') {
      fetchProposal();
      fetchDRepDetailsAndVotedType();
    }
  }, [id, getProposal]);

  if (!isMounted || isLoading || (!isError && isLoadingProposal)) {
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center bg-white dark:bg-dark-neutral-950">
        <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-primary-900 dark:border-white"></div>
      </div>
    );
  }

  if (error && 'status' in error && error.status === 503) {
    return <FiveOThree />;
  }

  // Only the proposal decides whether this page exists. Metadata validation
  // arriving late, or not at all, is not a missing page.
  if (!governanceAction || isError) {
    return <FourOFour />;
  }

  const renderVotedCard = (className: string) => {
    const isExpiredProposal =
      new Date(governanceAction.expireAt.time) < new Date();
    if (
      wallet.instance !== null &&
      isdRepRegistered &&
      !isEmpty(voteDetails.type) &&
      !isGetDRepDetailsAndVoteLoading &&
      !isExpiredProposal
    ) {
      return (
        <div
          className={cn(
            'flex flex-col items-center gap-4 rounded-xl bg-dark-neutral-800 p-4',
            className
          )}
        >
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-semantic-success-500 bg-semantic-success-100 shadow-Success-Shadow-1">
            <Check className="h-[18px] w-[18px] rounded-full bg-semantic-success-500" />
          </div>
          <p className="body18 text-center font-medium text-white">
            You voted
            <span className="font-semibold text-semantic-success-500">
              {' '}
              “{voteDetails.type.toUpperCase()}”{' '}
            </span>
            for this Governance Action.
          </p>

          {voteDetails.comment && !isEmpty(voteDetails.comment) && (
            <div className="flex w-full flex-col gap-2 rounded-xl bg-black p-3">
              <div
                className="flex cursor-pointer justify-between"
                onClick={() => {
                  setShowFullVoteComment((prev) => !prev);
                }}
              >
                <p className="body14 font-semibold leading-5">
                  Rationale for vote
                </p>
                <ChevronDown
                  className={`text-white transition-transform duration-500 ${
                    showFullVoteComment ? 'rotate-180' : ''
                  }`}
                  width={24}
                  height={24}
                />
              </div>

              <div
                className={cn(
                  'overflow-hidden transition-all duration-700 ease-in-out',
                  showFullVoteComment
                    ? 'max-h-[500px] opacity-100'
                    : 'max-h-[48px] opacity-80'
                )}
              >
                <p className="body14 font-normal leading-5 text-white md:max-w-[256px]">
                  {errorFetchingVoteComment
                    ? drepVoteCommentErrorMessage
                    : voteDetails.comment}
                </p>
              </div>
            </div>
          )}

          <Button
            className="flex w-full justify-center"
            buttonSize="xsmall"
            disabled={
              !isEmpty(pendingTransactions.pendingVotingTransaction.txId)
            }
            onClick={() =>
              openModal('CAST_VOTE', {
                votedType: voteDetails.type,
                votedComment: voteDetails.comment,
                proposalId:
                  governanceAction.createdAt.index !== null
                    ? `${governanceAction.createdAt.tx}#${governanceAction.createdAt.index}`
                    : governanceAction.createdAt.tx,
              })
            }
          >
            Recast Vote
          </Button>
        </div>
      );
    }

    return null;
  };

  const ccThreshold = (
    governanceAction.vote?.cc.quorum?.denominator
      ? governanceAction.vote.cc.quorum.numerator /
        governanceAction.vote.cc.quorum.denominator
      : 0
  ).toPrecision(2);

  const renderVoteCards = (className: string) => {
    if (governanceAction.vote) {
      return (
        <div className={cn('flex flex-col gap-5 sm:min-w-[288px]', className)}>
          {renderVotedCard('max-md:hidden')}
          <div className="flex flex-col gap-5">
            {(['DReps', 'SPOs', 'CC'] as Voter[]).map((voter: Voter) => {
              const areSPOsVoteEnabled =
                areSPOVoteTotalsDisplayed(governanceAction);
              const areCCVotesEnabled = areCCVoteTotalsDisplayed(
                governanceAction.proposal.type as GovernanceActionType
              );
              if (voter === 'SPOs' && !areSPOsVoteEnabled) return null;
              if (voter === 'CC' && !areCCVotesEnabled) return null;
              return (
                <VotedCard
                  key={voter}
                  voter={voter}
                  voteResponse={getVoteResponse(
                    voter,
                    governanceAction.vote as IProposalVoterType
                  )}
                  threshold={
                    (() => {
                      if (voter === 'CC') {
                        return governanceAction.proposal.type ===
                          GovernanceActionType.InfoAction
                          ? null
                          : Number(ccThreshold);
                      }
                      const votingThresholdKey = getGovActionVotingThresholdKey(
                        {
                          govActionType: governanceAction.proposal
                            .type as GovernanceActionType,
                          protocolParams: governanceAction.meta.protocolParams,
                          voterType: voter,
                        }
                      );
                      return (
                        votingThresholdKey && epochParams?.[votingThresholdKey]
                      );
                    })() as number
                  }
                />
              );
            })}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="flex w-full flex-col items-center bg-white dark:bg-dark-neutral-950">
      <div className="relative flex w-full max-w-[1440px] flex-col gap-6 px-4 pb-6 pt-6 md:px-[72px] md:pb-16 lg:px-[156px]">
        <div className="flex flex-col gap-3">
          <div
            onClick={() => router.replace('/governance_actions')}
            className="flex cursor-pointer items-center gap-3"
          >
            <BackArrow className="text-dark-neutral-900 dark:text-white" />
            <span className="body16 font-bold dark:text-white">Back</span>
          </div>
          <p className="h24 font-bold dark:text-white">Governance Actions</p>
        </div>
        <div className="flex w-full gap-6">
          <GovernanceActionDetailsLeftContainer
            hasServerHeader={hasServerHeader}
            isGetDRepDetailsLoading={isGetDRepDetailsAndVoteLoading}
            isdRepRegistered={isdRepRegistered}
            governanceAction={governanceAction}
            metadataResponse={metadataResponse}
            votedType={voteDetails.type}
            renderVotedCard={renderVotedCard}
            votesCard={renderVoteCards}
            dRepId={dRepId}
          />
          {renderVoteCards('max-md:hidden')}
        </div>
      </div>
    </div>
  );
}
